import { useState } from 'react';
import WorkflowMonitor from './WorkflowMonitor';
import { CI_PLATFORMS } from '../constants';
import { formatWhen, formatDay, describeAhead } from '../pipelineFormat';

// One idea's journey, in whatever state it is in. Presentational: the container
// (IdeaReview) owns the data. The owner's only decisions are here:
//   ready      -> look at each platform (reject any), then ONE "Approve & Schedule"
//   everything else is just watching.
const ICON = Object.fromEntries(CI_PLATFORMS.map((p) => [p.id, p.icon]));
const box = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 14 };
const chip = (bg, color) => ({ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: bg, color });

function PlatformRow({ p: platform, rejected, onToggleReject, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  // An older backend may not send previews yet: show the row without them rather than crash.
  const p = { ...platform, preview: platform.preview || { text: '', mediaUrl: null, mediaKind: 'none' } };
  const noMedia = p.media === 'failed' || (p.media === 'pending' && p.preview.mediaKind !== 'none');
  const blocked = noMedia || p.text !== 'ready';
  return (
    <li style={{ border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8, background: rejected ? '#f8fafc' : '#fff', opacity: rejected ? 0.6 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
          style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', padding: 0, minWidth: 0, font: 'inherit' }}>
          <span aria-hidden="true" style={{ fontSize: 20 }}>{ICON[p.id] || '■'}</span>
          <span style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{p.label} </span>
            {rejected && <span style={chip('#e2e8f0', '#475569')}>Rejected</span>}
            {!rejected && blocked && <span style={chip('#fee2e2', '#b91c1c')}>{p.text !== 'ready' ? 'Text missing' : 'Media failed'}: will be skipped</span>}
            {!rejected && !blocked && p.overBy > 0 && <span style={chip('#fef3c7', '#92400e')}>{p.overBy} characters over the limit</span>}
            <span style={{ display: 'block', fontSize: 12, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(p.preview.text || '').replace(/\s+/g, ' ').slice(0, 140)}</span>
          </span>
          <span aria-hidden="true" style={{ color: '#94a3b8' }}>{open ? '▲' : '▼'}</span>
        </button>
        <button type="button" onClick={() => onToggleReject(p.id)}
          style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer', border: `1px solid ${rejected ? '#16a34a' : '#fca5a5'}`, background: '#fff', color: rejected ? '#16a34a' : '#b91c1c', whiteSpace: 'nowrap' }}>
          {rejected ? 'Undo' : 'Reject'}
        </button>
      </div>
      {open && (
        <div style={{ padding: '0 12px 12px', display: 'grid', gap: 10 }}>
          {p.preview.mediaKind === 'image' && p.preview.mediaUrl && <img src={p.preview.mediaUrl} alt={`${p.label} image`} loading="lazy" style={{ maxWidth: 320, width: '100%', borderRadius: 8 }} />}
          {p.preview.mediaKind === 'video' && p.preview.mediaUrl && <video src={p.preview.mediaUrl} controls muted playsInline preload="metadata" style={{ maxWidth: 240, width: '100%', borderRadius: 8 }} />}
          {p.preview.mediaKind === 'none' && <div style={{ fontSize: 12, color: '#64748b' }}>Text only: no image or video for this platform.</div>}
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.55, color: '#1e293b' }}>{p.preview.text}</div>
        </div>
      )}
    </li>
  );
}

function ScheduleTable({ pipeline, onOpenCalendar }) {
  const s = pipeline.scheduleSummary || {};
  const rows = pipeline.platforms.filter((p) => p.schedule);
  const label = { scheduled: 'Scheduled', handed_to_youtube: 'Handed to YouTube’s weekly scheduler', waiting: 'Waiting for room (retried automatically)', failed: 'Failed', skipped: 'No connected account', rejected: 'Rejected', no_media: 'Skipped: no media' };
  const tone = (st) => (['scheduled', 'handed_to_youtube'].includes(st) ? '#166534' : st === 'rejected' ? '#64748b' : st === 'waiting' ? '#92400e' : '#b91c1c');
  return (
    <div>
      {s.lastAt ? (
        <div role="status" style={{ background: '#f0fdf4', border: '1px solid #16a34a', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 13, color: '#14532d' }}>
          <strong>{s.scheduled} post{s.scheduled === 1 ? '' : 's'} scheduled</strong>, from {formatDay(s.firstAt)} to {formatDay(s.lastAt)} ({describeAhead(s.lastAt)}).
        </div>
      ) : s.scheduled > 0 ? (
        <div role="status" style={{ background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 13, color: '#1e3a8a' }}>
          <strong>{s.scheduled} post{s.scheduled === 1 ? ' was' : 's were'} already approved and handed over for scheduling earlier.</strong> Their dates are on the Calendar.
        </div>
      ) : (
        <div role="alert" style={{ background: '#fffbeb', border: '1px solid #d97706', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 13, color: '#78350f' }}>
          <strong>Nothing was scheduled by this approval.</strong> The reasons are listed below.
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ textAlign: 'left', color: '#64748b', fontSize: 12 }}><th style={{ padding: '4px 6px' }}>Platform</th><th style={{ padding: '4px 6px' }}>When</th><th style={{ padding: '4px 6px' }}>Status</th></tr></thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '6px' }}>{ICON[p.id]} {p.label}</td>
              <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{p.schedule.scheduledAt ? formatWhen(p.schedule.scheduledAt) : '—'}</td>
              <td style={{ padding: '6px', color: tone(p.schedule.status) }}>{label[p.schedule.status] || p.schedule.status}{p.schedule.reason && p.schedule.status !== 'rejected' && (p.schedule.status !== 'scheduled' || !p.schedule.scheduledAt) ? `: ${p.schedule.reason}` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" onClick={onOpenCalendar} style={{ marginTop: 12, padding: '8px 14px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer' }}>
        See it on the Calendar {'→'}
      </button>
    </div>
  );
}

export default function PipelineCard({ pipeline, rejected = [], onToggleReject = () => {}, onApprove = () => {}, onRetry = () => {}, onOpenCalendar = () => {}, busy = false, error = null, openAll = false }) {
  const { status } = pipeline;
  const rejectedSet = new Set(rejected);
  const publishable = pipeline.platforms.filter((p) => !rejectedSet.has(p.id));

  return (
    <section style={box} aria-label={`Idea: ${pipeline.title}`}>
      <h3 style={{ margin: '0 0 10px', fontSize: 16, color: '#0f172a' }}>{pipeline.title}</h3>
      {error && <div role="alert" style={{ background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13, color: '#7f1d1d' }}>{error}</div>}

      <WorkflowMonitor steps={pipeline.monitor} compact={status === 'ready'} />

      {status === 'running' && (
        <p style={{ fontSize: 12, color: '#64748b', margin: '12px 0 0' }}>Working on the server. You can close this tab: you will get a phone alert when it is ready for your review.</p>
      )}

      {status === 'scheduling' && (
        <p style={{ fontSize: 12, color: '#64748b', margin: '12px 0 0' }}>Approved. The server is preparing and scheduling every platform. Nothing for you to do.</p>
      )}

      {status === 'ready' && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Review each platform. Open one to read it; reject only what you do not want.</div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {pipeline.platforms.map((p) => <PlatformRow key={p.id} p={p} rejected={rejectedSet.has(p.id)} onToggleReject={onToggleReject} defaultOpen={openAll} />)}
          </ul>
          <button type="button" disabled={busy || publishable.length === 0} onClick={onApprove}
            style={{ width: '100%', marginTop: 8, padding: '12px 0', fontSize: 15, fontWeight: 800, borderRadius: 10, border: 'none', cursor: busy || publishable.length === 0 ? 'not-allowed' : 'pointer', background: busy || publishable.length === 0 ? '#cbd5e1' : '#16a34a', color: '#fff' }}>
            {busy ? 'Approving…' : `✅ Approve & Schedule ${publishable.length} platform${publishable.length === 1 ? '' : 's'}`}
          </button>
          <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0', lineHeight: 1.5 }}>One click. Each platform is placed in its next free slot on your content calendar, and you get a phone alert when it is done. No further approvals.</p>
        </div>
      )}

      {status === 'completed' && <div style={{ marginTop: 16 }}><ScheduleTable pipeline={pipeline} onOpenCalendar={onOpenCalendar} /></div>}

      {status === 'failed' && (
        <div style={{ marginTop: 14 }}>
          <div role="alert" style={{ fontSize: 13, color: '#7f1d1d', background: '#fef2f2', border: '1px solid #dc2626', borderRadius: 8, padding: '8px 12px' }}>
            Something stopped this idea{pipeline.error ? `: ${pipeline.error}` : '.'} Trying again only redoes what is missing; nothing is duplicated.
          </div>
          <button type="button" disabled={busy} onClick={onRetry} style={{ marginTop: 10, padding: '9px 16px', fontSize: 14, fontWeight: 700, borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer' }}>{busy ? 'Starting…' : 'Try again'}</button>
        </div>
      )}
    </section>
  );
}
